import { Button, Classes } from "@blueprintjs/core"
import { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import CurrencyFilter from "../../../pages/Config/Dataset/CurrencyFilter"
import GeoFilter from "../../../pages/Config/Dataset/GeoFilter"
import SectorFilter from "../../../pages/Config/Dataset/SectorFilter"
import { DatasetFilters } from "../../../pages/Config/Dataset/types"
import useInfiniteDatasets from "../../Config/hooks/useInfiniteDatasets"
import { DashboardItemForm, DashboardUpdateCreateForm } from "../types/dashboardForm"
import { DialogBody, FooterContainer, StyledDialog } from "./BulkAddDatasetModal.styled"

interface BulkAddDatasetModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export const BulkAddDatasetModal = ({ isOpen, setIsOpen }: BulkAddDatasetModalProps) => {
  const [filters, setFilters] = useState<DatasetFilters>({})
  const [shouldFetchDatasets, setShouldFetchDatasets] = useState(false)

  const { setValue, getValues } = useFormContext<DashboardUpdateCreateForm>()

  const handleFilterChange = (newFilters: Partial<DatasetFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const { datasets, isFetching } = useInfiniteDatasets({
    withPaginationParam: false,
    ...filters,
    enabled: shouldFetchDatasets && Object.keys(filters).length > 0,
  })

  const handleCancel = () => {
    setFilters({})
    setShouldFetchDatasets(false)
    setIsOpen(false)
  }

  const handleAddSelected = () => {
    if (Object.keys(filters).length > 0) {
      setShouldFetchDatasets(true)
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (shouldFetchDatasets && datasets) {
      const currentItems = getValues("items") as DashboardItemForm[]
      const newDatasets = datasets.filter(
        dataset => !currentItems.some(item => item.dataset_id === dataset.id),
      )
      setValue(
        "items",
        [
          ...currentItems,
          ...newDatasets.map(dataset => ({
            dataset_id: dataset.id,
            dataset_name: dataset.name,
            dataset_long_name: dataset.long_name,
            company_id: null,
            company_main_instrument_ticker: null,
            fundamentals_source: null,
            fundamentals_periodicity: null,
            fundamentals_metric: null,
          })),
        ],
        {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        },
      )
      setFilters({})
      setShouldFetchDatasets(false)
    }
  }, [datasets, shouldFetchDatasets, setValue])

  return (
    <StyledDialog
      className="bp5-dark"
      isOpen={isOpen}
      title="Bulk Add Datasets"
      onClose={handleCancel}
    >
      <DialogBody className={Classes.DIALOG_BODY}>
        <FlexContainer flexDirection="column" gap="10px">
          <GeoFilter filters={filters} onFilterChange={handleFilterChange} />
          <CurrencyFilter filters={filters} onFilterChange={handleFilterChange} />
          <SectorFilter filters={filters} onFilterChange={handleFilterChange} />
        </FlexContainer>
      </DialogBody>
      <div className={Classes.DIALOG_FOOTER}>
        <FooterContainer>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            intent="primary"
            onClick={handleAddSelected}
            disabled={Object.keys(filters).length === 0}
            loading={shouldFetchDatasets && isFetching}
          >
            Add Selected
          </Button>
        </FooterContainer>
      </div>
    </StyledDialog>
  )
}
